<template>
  <div class="flex flex-col gap-1.5 p-1.5 rounded-xl border border-border-normal" style="width: 207px;">
    <div class="w-full flex items-center">
      <div class="w-8 h-8 flex items-center justify-center flex-shrink-0">
        <component :is="platformIcon" :width="16" :height="16" />
      </div>
      <BaseText type="label" size="sm" class="text-text-muted flex-grow ml-0.5">
        {{ platformName }}
      </BaseText>
      <!-- Issues Warning -->
      <div v-if="issuesWarning" class="relative">
        <div class="w-6 h-6 rounded-md bg-secondary-red-10 transition-colors duration-100 hover:bg-secondary-red-25 flex items-center justify-center"
        @mouseenter="showIssuesTooltip = true" @mouseleave="showIssuesTooltip = false">
          <WarningIcon class="text-secondary-red-300" />
        </div>
        <!-- Issues Tooltip -->
        <transition>
          <div v-if="showIssuesTooltip" class="tooltip px-1.5 py-0.5 rounded-md shadow-sm bg-neutral-700 pointer-events-none">
            <BaseText type="label" size="xs" class="text-white w-max">
              {{ issuesWarning }}
            </BaseText>
          </div>
        </transition>
      </div>
      <!-- Data Sync Status Indicator -->
      <div v-else-if="showSyncProgress" class="relative">
        <div class="relative w-5 h-5 flex items-center justify-center"
        @mouseenter="showSyncTooltip = true" @mouseleave="showSyncTooltip = false">
          <!-- Background circle SVG -->
          <svg width="20" height="20" class="absolute top-0 left-0">
            <circle stroke="#EAEFF3" stroke-width="1.5" fill="transparent" r="7" cx="10" cy="10" />
          </svg>
          <!-- Progress Circle Overlay -->
          <BaseProgressIndicatorRing 
            :key="`sync-progress-ring-${adAccount.id}`"
            ref="syncProgressRing"
            class="absolute top-0 left-0 z-10 text-secondary-green-100" 
            no-adjust
            no-frame-guard
          />
        </div>
        <!-- Sync Progress Tooltip -->
        <transition>
          <div v-if="showSyncTooltip" class="tooltip flex items-center gap-1 px-2 py-1.5 rounded-md shadow-sm bg-neutral-700 pointer-events-none w-max">
            <BaseText type="body" size="xs" style="color: rgba(255, 255, 255, 0.84)">
              Sync Progress
            </BaseText>
            <BaseText type="label" size="xs" class="text-white">
              {{ syncPercentString }}
            </BaseText>
          </div>
        </transition>
      </div>
      <!-- Actions Kebab -->
      <div v-if="!isLensGuest" class="w-8 h-8 flex items-center justify-center flex-shrink-0">
        <div class="relative">
          <button class="w-6 h-6 flex items-center justify-center rounded-md transition-colors duration-100"
          :class="showActionsMenu ? 'bg-neutral-25' : 'hover:bg-neutral-25'" @click="showActionsMenu = !showActionsMenu">
            <KebabMenuIcon />
          </button>
          <!-- Actions Menu -->
          <transition>
            <div v-if="showActionsMenu" class="actions-menu flex flex-col p-0.5 rounded-md bg-neutral-700 shadow-sm"
            v-on-clickaway="() => { showActionsMenu = false }">
              <button class="flex items-center gap-1 px-2 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-alpha-25"
              @click="$emit('remove')" :disabled="loadingRemove">
                <BaseLoadingSpinnerCircle v-if="loadingRemove" class="text-white" :duration="1.2" />
                <DeleteItemIcon v-else class="text-white" />
                <BaseText type="label" size="sm" class="text-white">
                  Remove
                </BaseText>
              </button>
            </div>
          </transition>
        </div>
      </div>
    </div>
    <div class="w-full flex flex-col gap-1.5 p-1.5">
      <BaseText type="label" size="sm" class="text-text-muted">
        {{ adAccount.name }}
      </BaseText>
      <BaseText type="label" size="xs" class="text-text-muted px-1 py-0.5 rounded-md bg-neutral-25 w-max max-w-full">
        {{ adAccount.companyName }}
      </BaseText>
      <BaseText type="body" size="xs" class="text-text-subdued max-w-full truncate">
        {{ adAccount.id.replace('act_', '') }}
      </BaseText>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway2'
import { platformNameMap, platformIconMap } from '@/utils/lens/dataSourceUtils'

// Icons
import KebabMenuIcon from '../../globals/Icons/KebabMenuIcon.vue'
import DeleteItemIcon from '../../globals/Icons/SidebarIcons/dropdowns/DeleteItemIcon.vue'
import WarningIcon from '../../globals/Icons/WarningIcon.vue'

export default {
  name: 'ConnectedAdAccountCard',
  mixins: [clickaway],
  components: {
    KebabMenuIcon,
    DeleteItemIcon,
    WarningIcon
  },
  props: {
    adAccount: {
      type: Object,
      required: true
    },
    issuesWarning: {
      type: String,
      default: null
    },
    loadingRemove: {
      type: Boolean,
      default: false
    },
    isLensGuest: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showActionsMenu: false,
      showIssuesTooltip: false,
      showSyncTooltip: false
    }
  },
  watch: {
    syncProgress: {
      handler () {
        this.$nextTick(() => { this.renderIndicatorProgress() })
      },
      immediate: true
    }
  },
  computed: {
    showSyncProgress () {
      return this.adAccount.dataAccuracy?.percent_diff > 10
    },
    syncProgress () {
      return (100 - Math.min(this.adAccount.dataAccuracy?.percent_diff ?? 0, 100))
    },
    syncPercentString () {
      return `${this.syncProgress.toFixed(0)}%`
    },
    platformIcon () {
      return platformIconMap[this.adAccount.platform]
    },
    platformName () {
      return platformNameMap[this.adAccount.platform]
    }
  },
  methods: {
    renderIndicatorProgress () {
      if (!this.$refs.syncProgressRing) return
      this.$refs.syncProgressRing.updateProgress(this.syncProgress / 100)
    }
  }
}
</script>

<style scoped>
.actions-menu {
  position: absolute;
  top: 50%;
  left: calc(100% + 4px);
  transform: translateY(-50%);
  z-index: 50;
  width: 100px;
}
.tooltip {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 51;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>